/**
 * Schema.org structured-data builders.
 *
 * These produce the JSON-LD blocks Google uses for rich results: breadcrumb
 * trails in the SERP, star ratings on salon pages, the FAQ accordion on the
 * homepage and the sitelinks search box for the brand query.
 */
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  LOGO_URL,
  CONTACT_EMAIL,
  ORGANIZATION,
  absoluteUrl,
} from "./seo.config";

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: ORGANIZATION.legalName,
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: LOGO_URL },
  description: DEFAULT_DESCRIPTION,
  email: CONTACT_EMAIL,
  areaServed: {
    "@type": "State",
    name: ORGANIZATION.areaServed,
  },
  address: {
    "@type": "PostalAddress",
    addressRegion: ORGANIZATION.addressRegion,
    addressCountry: ORGANIZATION.addressCountry,
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: CONTACT_EMAIL,
    areaServed: "IN",
    availableLanguage: ["en", "ml"],
  },
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-IN",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/shops?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/**
 * @param {{name: string, path: string}[]} trail  Ordered, excluding "Home".
 */
export const breadcrumbSchema = (trail = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Home", path: "/" }, ...trail].map(
    (crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })
  ),
});

/**
 * @param {{q: string, a: string}[]} faqs
 */
export const faqSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const webPageSchema = ({ title, description, path, type = "WebPage" }) => ({
  "@context": "https://schema.org",
  "@type": type,
  name: title,
  description,
  url: absoluteUrl(path),
  isPartOf: { "@id": `${SITE_URL}/#website` },
  inLanguage: "en-IN",
});

/**
 * Splits "12 Foo Road, Vennala, Ernakulam, Kerala 682028" into a PostalAddress.
 * Falls back to putting everything in streetAddress when the shape is unusual.
 */
const toPostalAddress = (address = "") => {
  const parts = String(address)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return {
      "@type": "PostalAddress",
      streetAddress: address || undefined,
      addressCountry: "IN",
    };
  }

  const last = parts[parts.length - 1];
  const postalMatch = last.match(/(\d{6})\s*$/);

  return {
    "@type": "PostalAddress",
    streetAddress: parts.slice(0, -2).join(", ") || parts[0],
    addressLocality: parts[parts.length - 2],
    addressRegion: last.replace(/\s*\d{6}\s*$/, "").trim() || undefined,
    postalCode: postalMatch ? postalMatch[1] : undefined,
    addressCountry: "IN",
  };
};

/** "09:00:00" -> "09:00" */
const toTime = (value) =>
  value ? String(value).slice(0, 5) : undefined;

/**
 * A salon listing. Emitted as HealthAndBeautyBusiness (a LocalBusiness
 * subtype), which is what earns the rating stars and hours panel in search.
 */
export const salonSchema = ({
  shop,
  services = [],
  rating,
  reviewCount,
  url,
  description,
}) => {
  if (!shop) return null;

  const openTime = toTime(shop.openTime || shop.openingHours?.open);
  const closeTime = toTime(shop.closeTime || shop.openingHours?.close);

  const schema = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    "@id": `${absoluteUrl(url)}#business`,
    name: shop.parlourName || shop.name,
    description:
      description ||
      `${shop.parlourName || "This salon"} on Orucom — book hair, skin, nail and beauty services online.`,
    url: absoluteUrl(url),
    image: shop.shopImage || undefined,
    telephone: shop.phone || undefined,
    address: toPostalAddress(shop.address),
    priceRange: "₹₹",
    currenciesAccepted: "INR",
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
  };

  if (shop.latitude && shop.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: Number(shop.latitude),
      longitude: Number(shop.longitude),
    };
  }

  if (openTime && closeTime) {
    schema.openingHoursSpecification = [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: openTime,
        closes: closeTime,
      },
    ];
  }

  // Google rejects AggregateRating without a real review/rating count, so only
  // emit it when the API actually gave us one.
  if (rating > 0 && reviewCount > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(rating).toFixed(1),
      reviewCount: Number(reviewCount),
      bestRating: 5,
      worstRating: 1,
    };
  }

  if (services.length) {
    schema.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: `Services at ${shop.parlourName || "this salon"}`,
      itemListElement: services.slice(0, 30).map((service) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: service.name || service.serviceName,
          category: service.category?.name || undefined,
        },
        price: service.price != null ? String(service.price) : undefined,
        priceCurrency: "INR",
      })),
    };
  }

  return schema;
};

/**
 * The salon-listing pages. An ItemList tells Google the page is a directory
 * and which entities it points at.
 */
export const itemListSchema = ({ name, description, path, items = [] }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url: absoluteUrl(path),
  isPartOf: { "@id": `${SITE_URL}/#website` },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.slice(0, 50).map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: absoluteUrl(item.url),
      name: item.name,
    })),
  },
});
